//common
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import { Download } from "react-bootstrap-icons";

//deck.gl
import { DeckGL } from "@deck.gl/react";
import { LinearInterpolator, OrthographicView, OrthographicViewState, PickingInfo } from "@deck.gl/core";
import { GeoJsonLayer, TextLayer } from "@deck.gl/layers";
import type { Feature, FeatureCollection, Geometry, Polygon } from "geojson";
import { DataFilterExtension } from "@deck.gl/extensions";

//satellite
import { AbandonedVehicleReviewStatus, AbandonedVehicleStatus } from "./api/Network";
import { AbandonedVehicleLoaderData, Match, AreaProps } from "./loaders/abandonedVehicleLoader";
import { Button } from "./components/Button";
import { showAbandonedVehicleStatus } from "./api/EnumUtils";
import { downloadCsv } from "./Csv";
import { CompareFilter, FilterBody, StatusFilterModal } from "./components/CompareFilterModal";
import { LocationInfoModal } from "./LocationInfoModal";
import { areaCodes, lineMidpoint, getReviewStatusColor, getCallToAction, createAerialTileLayer, createRoadLabelsLayer, RoadLabelData, hilbertD } from "./AbandonedVehicleListPageUtil";
import { RectSize, Pixel, pixelToLatLng, PixelOffset } from "./common/Geography";
import { createRoundedVehiclePolygon, Obb } from "./common/Geometry";
import { DisplayVehicleType, AbandonedVehicleSituation as Situation } from "./loaders/abandonedVehicleCompressed";
import { AbandonedVehicle, AerialImage } from "./api/Application";
import { List, ListImperativeAPI } from "react-window";
import { linearInterpolate, quantize } from "./common/Numeric";
import { AbandonedVehicleReviewDialog } from "./AbandonedVehicleReviewDialog";
import { flushSync } from "react-dom";
import { Row } from "./AbandonedVehicleSideBarItem";
import { ButtonGroup } from "react-bootstrap";
import { Button as BsButton } from "react-bootstrap"
import { getReviewStatusIcon } from "./components/AbandonedVehicleStatusIconNew";
import React from "react";
import { AbandonedVehicleHelpPane, TourStep } from "./AbandonedVehicleListPageHelpModal";
import { PiQuestionBold } from "react-icons/pi";
import { IoSettings } from "react-icons/io5";

const initialValue: CompareFilter = {
    areaFilters: [],
    typeFilters:         [null, DisplayVehicleType.Car, DisplayVehicleType.Pickup, DisplayVehicleType.Suv],
    situationFilters:    [Situation.Street],
    statusFilters:       [AbandonedVehicleStatus.ReviewedVerified],
    reviewStatusFilters: [AbandonedVehicleReviewStatus.New, AbandonedVehicleReviewStatus.NotActionable, AbandonedVehicleReviewStatus.Actionable],
    confThr:             0.0,
    similarityThr:       0.0,
}

export function AbandonedVehicleListPage() {
    const nav = useNavigate();
    const [showHelp,     setShowHelp]      = useState<boolean>( true );
    const [helpStep,     setHelpStep]      = useState<number>( 0 );
    const [hideBoxes,    setHideBoxes]     = useState<boolean>( false );
    const [showA,        setShowA ]        = useState<boolean>( false );
    const [filters,      setFilters]       = useState<CompareFilter>( initialValue );
    const {
        runId,
        imageA,
        imageB,
        vehicles: initVehicles,
        savedOffset,
        areas,
        roads,
    } = useRouteLoaderData( "run" ) as AbandonedVehicleLoaderData;
    const selectedMatch = useRouteLoaderData( "run-vehicle" ) as AbandonedVehicle | undefined;
    const [vehicles,      setVehicles]    = useState<Match[]>( initVehicles );
    const [features,      setFeatures]    = useState<any>();
    const [showFilters,   setShowFilters] = useState<any>();
    const [offset,        setOffset]      = useState<PixelOffset>( savedOffset as PixelOffset ); // dx, dy in pixels
    const [lineWidth,     setLineWidth]   = useState<number>( 0.5 );
    const [zoom,          setZoom]        = useState<number>( () => selectedMatch !== undefined ? 2 : -3 );    
    const [visibleA,      setVisibleA]    = useState<boolean>( showA );
    const listRef       = useRef<ListImperativeAPI>( null );
    const aWidth        = Math.ceil( imageA.width  / 512.0 ) * 512;
    const aHeight       = Math.ceil( imageA.height / 512.0 ) * 512;
    const bWidth        = Math.ceil( imageB.width  / 512.0 ) * 512;
    const bHeight       = Math.ceil( imageB.height / 512.0 ) * 512;
    const linePad       = selectedMatch ? 0.25 : 0.25;
    const mapPaneRef = useRef<HTMLDivElement>( null );    

    // function getTarget( match: Obb ): [number, number, number] {
    //     return [ match.x, match.y, 0 ];
    // }

    function getTarget( match: Obb, zoom: number = TARGET_ZOOM ): [number, number, number] {
        const scale = 2 ** zoom;
        const paneH = mapPaneRef.current?.clientHeight ?? 0;
        const biasPx = paneH * ( 3 / 5 - 1 / 2 );   // = paneH / 10
        return [match.x, match.y - biasPx / scale, 0];
    }

    function getMapCenter( imageB: AerialImage ): [number, number, number] {
        return [
            Math.round( imageB.width  / 2 ),
            Math.round( imageB.height / 2 ),
            0
        ];
    }

    const TARGET_ZOOM = 2;
    function getTargetViewState( target: [number, number, number], zoom: number ): OrthographicViewState {
        return {
            target:                 target,
            zoom:                   zoom,
            transitionInterpolator: new LinearInterpolator( ["target", "zoomX", "zoomY"] ),
            transitionDuration:     1000,
            transitionEasing:       t => t * ( 2 - t ),
        };
    }

    // view state for orthographic Deck view
    const [viewState, setViewState] = useState<OrthographicViewState>( () => {
        const defaultViewState: OrthographicViewState = {
            target: getMapCenter( imageB ),
            zoom:   -4
        };
        if( selectedMatch === undefined ) {
            // console.log( "overwriting with default" );
            return defaultViewState;
        }
        const target = getTarget( selectedMatch );
        // console.log( "overwriting viewState with target" );
        return { target: target, zoom: TARGET_ZOOM };
    } );
    const cameraRef     = useRef<OrthographicViewState>( viewState );
    const lanczosWeight = zoom > 1.5 ? 2.0 
                        : zoom > 0.5 ? 2.0 : 1.0;

    const setSelectedMatch = useCallback( ( match: Match | undefined ) => {
        if( selectedMatch === undefined && match === undefined ) {
            return;
        }
        const path = match !== undefined
            ? `/detect/${runId}/${match.abandonedVehicleId}`
            : `/detect/${runId}`;
        nav( path, { replace: true } );
    }, [selectedMatch] );

    // click handler for Deck canvas -> mimic MapEvtHandlers click behavior
    async function onDeckClick( info: any ) {
        if( !info?.coordinate ) {
            return;
        }
        // if clicked on nothing (no picked object), open google maps + fetch nearby businesses
        if( info && !info.object ) {
            const coord = info.coordinate; // [x, y, z]
            if( !coord )
                return;
            const [ x, y ] = coord;
            const [lat, lng] = pixelToLatLng( x, y, imageB );
            await navigator.clipboard.writeText(`${lat},${lng}`);
            // window.open( `https://www.google.com/maps?q=${lat},${lng}`, "_blank" );
            // fetchNearbyBusinesses( lat, lng ).then( x => setFeatures( x ) );
        }
    }

    const handleDownloadCsv = () => {
        const headers = ["ID", "Lat", "Lon", "Confidence", "Similarity", "Status", "Address"];
        const rows = resultsFiltered.map( v => {
            const [lat, lng] = pixelToLatLng( v.x, v.y, imageB );
            return [
                v.abandonedVehicleId,
                lat,
                lng,
                v.confidence ?? "",
                v.similarity ?? "",
                `"${v.attributes.address ?? ""}"`,
                showAbandonedVehicleStatus( v.status?.status || AbandonedVehicleStatus.Candidate )
            ];
        } );
        downloadCsv( `AbandonedVehiclesFromRun${runId}.csv`, headers, rows );
    };

    useEffect( () => {
        const handleKeyDown = ( e: KeyboardEvent ) => {
            const key = e.key;
            switch( key ) {
                case "F1":     setShowHelp( true ); e.preventDefault(); break;
                case "Escape": setSelectedMatch( undefined );           break;
                case "1":      setShowA( true  );                       break;
                case "2":      setShowA( false );                       break;
            }
        };
        window.addEventListener( "keydown", handleKeyDown );
        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [selectedMatch] );

    const tileOpac = linearInterpolate( -3, 0, 0.40, 1, zoom );
    const prevTileLayer = useMemo( () => createAerialTileLayer( {
        id:            "img-a",
        image:         imageA,
        imageRef:      { offset: offset, baseImage: imageB },
        // visible:       visibleA,
        opacity:       showA === true ? tileOpac : 0.0,
        lanczosWeight: lanczosWeight,
        // onEnd: () => setVisibleA( showA ),

    } ), [ showA, visibleA, imageA, aWidth, aHeight, offset, lanczosWeight, tileOpac ] );

    const tileLayer = useMemo( () => createAerialTileLayer( {
        id:            "img-b",
        image:         imageB,
        // visible:       !visibleA,
        opacity:       showA === false ? tileOpac : 0.0,
        lanczosWeight: lanczosWeight,
        // onEnd: () => setVisibleA( showA ),
    } ), [ showA, visibleA, imageB, bWidth, bHeight, lanczosWeight, tileOpac ] );

    const preppedVehicles = useMemo( () => {
        return vehicles.map( v => ({
            ...v,
            attributes: { ...v.attributes, area: v.attributes.area ?? -1 }
        }) );
    }, [vehicles] );

    const polygons: FeatureCollection<Polygon, Match> = useMemo( () => {
        const { situationFilters, statusFilters, reviewStatusFilters } = filters;
        const res = preppedVehicles
            .filter( v => situationFilters.length > 0 ? situationFilters.includes( v.displaySituation ) : true )
            .filter( v => statusFilters.includes( v.status.status ) )
            .filter( v => reviewStatusFilters.length > 0 ? reviewStatusFilters.includes( v.reviewStatus?.status ?? null ) : true )
            .map( ( m ) => {
                const coords = createRoundedVehiclePolygon( m, linePad );
                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Polygon" as const,
                        coordinates: [coords],
                    },
                    properties: { ...m },
                };
            } );
        return {
            type: "FeatureCollection" as const,
            features: res,
        };
    }, [preppedVehicles, filters.statusFilters, filters.typeFilters, filters.situationFilters, filters.reviewStatusFilters, linePad] );

    const lineWidthCalc = linearInterpolate( -3, 0, 7, 2, zoom );
    const vehiclesLayer = useMemo( () => {
        return new GeoJsonLayer( {
            id: "vehicles",
            data: polygons,
            pickable: true,
            autoHighlight: true,
            highlightColor: [0, 255, 255, 100],
            filled: true,
            stroked: true,
            lineJointRounded: true,
            getFillColor: [0, 0, 0, 0],
            getLineColor: ( f: Feature<Geometry, Match> ) => f.properties.reviewStatus?.status !== undefined
                ? getReviewStatusColor( f.properties.reviewStatus.status as AbandonedVehicleReviewStatus ) : [0xff, 0xff, 0xff, 0xff],
            getLineWidth: lineWidth,
            lineWidthMinPixels: lineWidthCalc,
            coordinateSystem: "cartesian",
            extensions: [new DataFilterExtension( { filterSize: 2, categorySize: 2 } )],
            visible: !hideBoxes,
            getFilterCategory: ( f: Feature<Geometry, Match> ) => [
                f.properties.attributes.area,    // number id
                f.properties.displayVehicleType, // number id
            ],
            filterCategories: [
                filters.areaFilters.length > 0 ? filters.areaFilters : areaCodes.map( a => a.id ).concat( -1 ), // all area IDs
                filters.typeFilters.length > 0 ? filters.typeFilters : Object.values( DisplayVehicleType ).filter( v => typeof v === 'number' ),  // all type IDs
            ],
            // order matters: one slot per threshold
            getFilterValue: ( f: Feature<Geometry, Match> ) => [
                f.properties.confidence ?? 0, // [0, 1]
                f.properties.similarity ?? 0, // [0, 1]
            ],
            filterRange: [
                [filters.confThr,       1.0], // similarity above threshold
                [filters.similarityThr, 1.0], // similarity above threshold
            ],
            updateTriggers: {
                getFilterValue: [
                    filters.confThr,
                    filters.similarityThr,
                ],
                getFilterCategory: [],
            },
            onClick: ( info, event ) => {
                if( info && info.object && info.object.properties ) {
                    const m = info.object.properties as Match;
                    handleMatchClick( m );
                }
            },
        } );
    }, [vehicles, filters, hideBoxes, lineWidthCalc, linePad] );

    //fill color for the areas
    const opac       = linearInterpolate( -3.0, -1.5, 0.20, 0.0, zoom ) * 255;
    const borderOpac = linearInterpolate( -3.0, -1.5, 1.00, 0.0, zoom ) * 255;
    const areasLayer = useMemo( () => {
        return new GeoJsonLayer<AreaProps>( {
            id:               "areas",
            data:             areas,
            pickable:         zoom <= -1.5,
            autoHighlight:    true,
            highlightColor:   [200, 255, 200, opac],
            filled:           true,
            stroked:          true,
            lineJointRounded: true,
            getFillColor: x => {
                const [r, g, b, a] = x.properties.color;
                return filters.areaFilters.includes( x.properties.id ) ? [r, g, b, opac] : [0, 0, 0, 0];
            },
            getLineColor: x => {
                const [r, g, b, a] = x.properties.color;
                return filters.areaFilters.includes( x.properties.id ) ? [r, g, b, borderOpac] : [r, g, b, 64];
            },
            onClick: ( info: PickingInfo ) => {
                const area = info.object.properties as AreaProps;
                const chk = filters.areaFilters.includes( area.id );
                const chg = chk
                    ? filters.areaFilters.filter( a => a !== area.id )
                    : [...( filters.areaFilters ), area.id];
                setFilters( { ...filters, areaFilters: chg } );
            },
            getLineWidth: lineWidth,
            lineWidthMinPixels: 2,
            updateTriggers: {
                getLineColor:   [filters.areaFilters, opac, zoom],
                getFillColor:   [filters.areaFilters, opac, zoom],
                highlightColor: [filters.areaFilters, opac, zoom]
            },
            coordinateSystem: "cartesian",
        } );
    }, [areas, filters.areaFilters, zoom, opac] );

    const areaLabelData = useMemo( () => {
        return [
            {
                "name": "Central Business District",
                "code": "CBD",
                "position": [
                    4892.190064657341,
                    7912.468137640576
                ],
                "id": 2
            },
            {
                "name": "Musser Park",
                "code": "MP",
                "position": [
                    6401.648411440308,
                    6809.1621042340685
                ],
                "id": 8
            },
            {
                "name": "Mussertown",
                "code": "Mt",
                "position": [
                    6980.585099650769,
                    9753.825091959217
                ],
                "id": 9
            },
            {
                "name": "Churchtowne",
                "code": "Ct",
                "position": [
                    6623.231100112153,
                    11009.530555984884
                ],
                "id": 4
            },
            {
                "name": "Southside",
                "code": "SS",
                "position": [
                    5105.2975056584755,
                    11449.630019082751
                ],
                "id": 12
            },
            {
                "name": "Cabbage Hill",
                "code": "Ca",
                "position": [
                    3347.0795667832813,
                    10044.716808947775
                ],
                "id": 1
            },
            {
                "name": "Prospect Heights",
                "code": "PH",
                "position": [
                    2268.785770061992,
                    12188.781433197571
                ],
                "id": 10
            },
            {
                "name": "West End",
                "code": "WE",
                "position": [
                    1258.1631534364938,
                    9575.736020707971
                ],
                "id": 15
            },
            {
                "name": "Chestnut Hill",
                "code": "CH",
                "position": [
                    2827.7230405510973,
                    6766.0085449808585
                ],
                "id": 3
            },
            {
                "name": "College Park",
                "code": "CP",
                "position": [
                    667.9185196018145,
                    5677.222172039208
                ],
                "id": 5
            },
            {
                "name": "Stadium District",
                "code": "SD",
                "position": [
                    2469.710127050889,
                    3421.757242921576
                ],
                "id": 13
            },
            {
                "name": "Ross",
                "code": "Ro",
                "position": [
                    6219.037787703812,
                    3607.24546712347
                ],
                "id": 11
            },
            {
                "name": "East Side",
                "code": "ES",
                "position": [
                    9924.046961388241,
                    5962.240984607261
                ],
                "id": 7
            },
            {
                "name": "Stevens",
                "code": "St",
                "position": [
                    9110.930632764203,
                    9748.296929638209
                ],
                "id": 14
            },
            {
                "name": "Conestega Heights",
                "code": "Co",
                "position": [
                    8836.447753284854,
                    12315.351872485498
                ],
                "id": 6
            }
        ];        
    }, [areas] );

    const areaLabelsLayer = useMemo( () => {
        const labelOpac = Math.round( linearInterpolate( -2.0, -0.0, 1.0, 0.0, zoom ) * 255 );
        return new TextLayer( {
            id:               "area-labels",
            data:             areaLabelData,
            pickable:         false,
            getPosition:      d => d.position,
            getText:          d => d.name.split( " " ).join( "\n" ),
            getSize:          20,
            sizeUnits:        "pixels",
            getColor:         d => filters.areaFilters.length === 0 || filters.areaFilters.includes( d.id )
                                    ? [255, 255, 255, labelOpac]
                                    : [255, 255, 255, Math.round( labelOpac * 0.6 )],
            outlineWidth:     2,
            outlineColor:     [0, 0, 0, labelOpac],
            // fontSettings:     { sdf: true },
            getTextAnchor:    "middle",
            getAlignmentBaseline: "center",
            getAngle:         6,
            billboard:        true,
            visible:          labelOpac > 0,
            coordinateSystem: "cartesian",
            updateTriggers: {
                getColor: [filters.areaFilters, labelOpac],
            },
        } );
    }, [areaLabelData, zoom, filters.areaFilters] );

    const resultsAll = useMemo( () => {
        return vehicles.toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
    }, [vehicles] );

    const resultsFiltered = useMemo( () => {
        const order = 16;
        const scale = ( 1 << order ) / Math.max( imageB.width, imageB.height );
        const key = ( v: Match ) =>
            hilbertD( order, Math.floor( v.x * scale ), Math.floor( v.y * scale ) );

        const { areaFilters, typeFilters, situationFilters, statusFilters,
            reviewStatusFilters, confThr, similarityThr } = filters;

        return resultsAll
            .filter( v => areaFilters.length      > 0 ? areaFilters.includes( v.attributes.area       ) : true )
            .filter( v => typeFilters.length      > 0 ? typeFilters.includes( v.displayVehicleType    ) : true )
            .filter( v => situationFilters.length > 0 ? situationFilters.includes( v.displaySituation ) : true )
            .filter( v => statusFilters.includes( v.status.status ) )
            .filter( v => reviewStatusFilters.length > 0 ? reviewStatusFilters.includes( v.reviewStatus?.status ?? null ) : true )
            .filter( v => !confThr       || ( v.confidence > confThr ) )
            .filter( v => !similarityThr || ( v.similarity > similarityThr ) )
            .map( v => [key( v ), v] as const )
            .sort( ( a, b ) => a[0] - b[0] )
            .map( ( [, v] ) => v );
    }, [resultsAll, filters] );

    useEffect( () => {
        if( selectedMatch === undefined || listRef.current === null ) {
            return;
        }
        const id  = selectedMatch.abandonedVehicleId;
        const ind = resultsFiltered.findIndex( v => v.abandonedVehicleId === id );
        if( ind === -1 ) {
            setSelectedMatch( undefined );
            return;
        }
        listRef.current.scrollToRow( { index: ind, align: "smart" } );
    }, [selectedMatch, resultsFiltered] );

    const handleItemClick = useCallback( ( match: Match ) => {
        setSelectedMatch( match );

        const pane = mapPaneRef.current;
        if( !pane ) { return; }

        // commit #1: re-anchor the prop at the real current camera (visual no-op)
        flushSync( () => {
            const { zoomX, zoomY, transitionInterpolator, transitionDuration, transitionEasing, ...current } =
                cameraRef.current as any;
            setViewState( {
                ...current,
                zoom: typeof zoomX === "number" ? zoomX : current.zoom   // fold live zoom back in
            } );
        } );
        // commit #2: fly from there
        setViewState( getTargetViewState( getTarget( match ), TARGET_ZOOM ) );
    }, [setSelectedMatch, setViewState] );

    const handleMatchClick = useCallback( async ( match: Match ) => {
        handleItemClick( match );
    }, [vehicles, resultsAll] );

    const labelData = useMemo<RoadLabelData[]>( () => {
        if( !roads || roads.type !== "FeatureCollection" ) return [];
        return roads.features
            .filter( f => f.properties.name )
            .map( ( f: any ) => {
                const geom = f.geometry;
                const line = geom.type === "MultiLineString" ? geom.coordinates[0] : geom.coordinates;
                const { position, angle } = lineMidpoint( line );
                return {
                    name: f.properties.name,
                    position: position as Pixel,
                    angle };
            } );
    }, [roads] );

    const roadLabels = useMemo( () => createRoadLabelsLayer( { labelData, zoom } ), [labelData, zoom] );
    const maxBounds: RectSize = useMemo( () => {
        return [imageB.width, imageB.height] as RectSize;
    }, [] );

    const views = useMemo( () => new OrthographicView( {
        controller: {
            maxBounds:       [[0, 0], maxBounds],
            doubleClickZoom: false
        }
    } ), [] );

    const verifiedCount = useMemo( () => {
        return resultsAll.filter( v => v.status.status === AbandonedVehicleStatus.ReviewedVerified ).length;
    }, [resultsAll] );

    const counts = useMemo( () => {
        const countMap = new Map<AbandonedVehicleReviewStatus, number>();
        countMap.set( AbandonedVehicleReviewStatus.New,           0 );
        countMap.set( AbandonedVehicleReviewStatus.NotActionable, 0 );
        countMap.set( AbandonedVehicleReviewStatus.Actionable,    0 );
        for( const item of resultsFiltered ) {
            if( item.reviewStatus !== null ) {
                const curCount = countMap.get( item.reviewStatus.status )!;
                countMap.set( item.reviewStatus.status, curCount + 1 );
            }
        }
        return countMap;
    }, [resultsFiltered] );
    
    const setArea = ( ids: number[] ) => setFilters( f => ( { ...f, areaFilters: ids } ) );
    const tourSteps: TourStep[] = useMemo( () => [
        {
            title:  "View Before Image",
            body:   "Press 1 to view the *before* image",
            onEnter: () => {
                setViewState( getTargetViewState( getTarget( resultsFiltered[0] ), TARGET_ZOOM ) );
                setShowA( true );
            },
            onLeave: () => {
                setShowA( false );
            }
        },
        {
            title:  "View After Image",
            body:   "Press 2 to view the *after* image",
            onEnter: () => {
                setViewState( getTargetViewState( getTarget( resultsFiltered[0] ), TARGET_ZOOM ) );
                setShowA( false );
            },
            onLeave: () => {
                setShowA( true );
            }
        },
        {
            title:  "Zoom Out",
            body:   "Zoom out to see the neighborhoods of the city",
            onEnter: () => {
                setViewState( getTargetViewState( getMapCenter( imageB ), -4 ) );
                // setArea( areaCodes.map( x => x.id ) );
            },
            onLeave: () => {
                setArea( [] );
            }
        },
        {
            title: "Filter by Area",
            body:  "Clicking an area polygon limits results to that area",
            onEnter: () => {
                setViewState( getTargetViewState( getMapCenter( imageB ), -4 ) );
                setArea( [areaCodes[1].id] );
            },
            onLeave: () => {
                // setArea( [] );
            }
        },
        {
            title: "Zoom In",
            body:  "Zoom in to see abandoned vehicles.",
            onEnter: () => {
                const first = resultsFiltered[0];
                if( first ) setViewState( getTargetViewState( getTarget( first ), TARGET_ZOOM ) );
            },
        },
        {
            title: "Review Abandoned Vehicles",
            body: "Click on abandoned vehicles to allow or enforce parking",
            onEnter: () => {
                const first = resultsFiltered[0];
                if( first ) setSelectedMatch( first );
            },
            onLeave: () => {
                setSelectedMatch( undefined );
            }
        },
    ], [imageB, resultsFiltered, setViewState, getTargetViewState] );

    const showSideBar = !showHelp;
    return <div className="h-100">
        <LocationInfoModal
            features={features}
            onClose={() => setFeatures( undefined )} />

        <StatusFilterModal 
            show={showFilters} 
            onHide={ () => setShowFilters( false ) }
            vehicleCount={resultsFiltered.length}
            filters={filters}
            fullscreen="lg-down"
            setFilters={setFilters} />
        
        <div id="capture-map-page">
            <div id="capture-list">
                {!showSideBar && <AbandonedVehicleHelpPane step={helpStep} steps={tourSteps} setStep={setHelpStep} onClose={ () => setShowHelp( false ) } />}
                {showSideBar && <React.Fragment>
                    <div className="position-relative bg-body-tertiary border border-end-0 d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                        <div className="flex-grow-1 ps-2 fs-5">
                            <span className="d-none d-xxl-inline">Showing </span>{resultsFiltered.length} of {verifiedCount} Vehicles
                        </div>
                        <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={handleDownloadCsv}>
                            <Download />
                        </Button>
                    </div>
                    <div className="h-100" style={{ minHeight: 0, display: "grid", gridTemplateRows: "min-content 1fr"}}>
                        <div className="pt-2 px-2">
                            <FilterBody vehicleCount={resultsFiltered.length} filters={filters} setFilters={setFilters} />
                        </div>
                        <div className="h-100 px-2 y-scroll-auto" style={{ minHeight: 0 }}>
                            <List
                                listRef={listRef}
                                rowComponent={Row}
                                rowCount={resultsFiltered.length}
                                rowHeight={124}
                                rowProps={{
                                    selectedId: selectedMatch?.abandonedVehicleId,
                                    vehicles: resultsFiltered,
                                    onClick: handleItemClick
                                }}
                                overscanCount={6} />
                        </div>
                    </div>
                </React.Fragment>}
            </div>
            <div className="image-viewer h-100 overflow-hidden position-relative"
                style={{ backgroundColor: "black" }}
                ref={mapPaneRef}>
                {mapPaneRef.current !== null && <AbandonedVehicleReviewDialog
                    paneSize={[mapPaneRef.current?.clientWidth, mapPaneRef.current?.clientHeight] as RectSize}
                    imageA={imageA}
                    imageB={imageB}
                    show={selectedMatch}
                    setShow={ () => setSelectedMatch( undefined ) }
                    save={ v => {
                        const updated = [...vehicles.filter( iv => v.abandonedVehicleId != iv.abandonedVehicleId ), v];
                        setVehicles( updated );
                    }} />}
                <div className="position-relative ms-2 mt-2 d-flex gap-1">                    
                    <BsButton
                        style={{ zIndex: 3 }}
                        className="d-block d-lg-none bg-body text-primary fs-4 px-3 d-flex align-items-center"
                        variant="outline-primary"
                        onClick={ () => {
                            setShowFilters( true );
                        }}>
                        <IoSettings />
                    </BsButton>                    
                    <BsButton
                        style={{ zIndex: 3 }}
                        className="d-none d-lg-block bg-body text-primary fs-4 px-3 d-flex align-items-center"
                        variant="outline-primary"
                        onClick={ () => {
                            setHelpStep( 0 );
                            setShowHelp( true );
                        }}>
                        <PiQuestionBold />
                    </BsButton>
                    <ButtonGroup className="top-0 start-0 bg-body" style={{ zIndex: 3 }}>
                        {[1, 2].map( ( x, n ) => {
                            return <BsButton
                                key={x}
                                className="fs-4 px-4"
                                variant={showA === ( n === 0 ) ? "primary" : "outline-primary"}
                                onClick={() => setShowA( n === 0 )}>
                                {x}
                            </BsButton>;
                        } )}
                    </ButtonGroup>
                </div>
                <div className="position-absolute bottom-0 right-0 ms-2 mb-2 rounded gap-2 d-flex flex-column" style={{ zIndex: 2 }}>
                    {[
                        AbandonedVehicleReviewStatus.New,
                        AbandonedVehicleReviewStatus.NotActionable,
                        AbandonedVehicleReviewStatus.Actionable,
                    ].map( x => {
                        const { icon, size, color, borderColor } = getReviewStatusIcon( x );
                        return <div className={`bg-body border shadow align-items-center gap-2 p-3 rounded-3 ${borderColor}`}
                            style={{ display: "grid", gridTemplateColumns: "max-content 2.5em max-content" }}>
                            <div className="text-end">{React.cloneElement( icon, { className: `${size} ${color}` } )}</div>
                            <div className="text-end">
                                {counts.get( x )!}
                            </div>
                            <div className="d-none d-md-block">{getCallToAction( x )}</div>
                        </div>
                    })}
                </div>
                <DeckGL
                    views={views}
                    layers={[
                        prevTileLayer,
                        tileLayer,
                        areasLayer,
                        roadLabels,
                        vehiclesLayer,
                        areaLabelsLayer
                    ]}
                    initialViewState={ viewState }
                    onViewStateChange={( { viewState: vs, interactionState } ) => {
                        const { isZooming = false, inTransition = false } = interactionState ?? {};
                        if( !inTransition && isZooming && selectedMatch !== undefined ) {
                            setSelectedMatch( undefined );
                        }
                        cameraRef.current = vs;
                        const z = ( vs as any ).zoomX ?? ( Array.isArray( vs.zoom ) ? vs.zoom[0] : vs.zoom );
                        if( typeof z === "number" ) {
                            const q = quantize( 0.05, z );
                            queueMicrotask( () => setZoom( prev => prev !== q ? q : prev ) );
                        }
                    }}
                    onClick={onDeckClick}
                    style={{ backgroundColor: "black" }}
                    onDrag={ () => {
                        // if( selectedMatch !== undefined ) {
                        //     return;
                        // }
                        setSelectedMatch( undefined );
                    }}
                    getCursor={( { isDragging, isHovering } ) => {
                        if( isDragging ) { return "grabbing";  }
                        if( isHovering ) { return "pointer";   }
                        return "grab";
                    }}>
                    {/* <div className="map-center-debug-dot" /> */}
                </DeckGL>
            </div>
        </div>
    </div>;
}

